import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { Box, Grid, TextField, Button } from "@mui/material";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Modal from "../../../../Components/Modal/Modal";
import { useStyle } from "../../../HR/Pages/Attendance/attendance-styles";
import AutoCompleteSearch from "../../../../Components/AutoCompleteSearch/AutoCompleteSearch";
import api from "../../../../Utils/interceptors";
import { successNotify, errorNotify } from "../../../../Utils/toast";

const useButtonStyle = makeStyles((theme) => ({
  cancelBtn: {
    background: "red !important",
    color: "white !important",
    padding: "8px 20px !important",
    borderRadius: "25px !important",
    boxShadow: "none !important",
  },
  createLeaveBtn: {
    background: "linear-gradient(to bottom, #0097d9, #00b1ff) !important",
    color: "white !important",
    padding: "8px 20px !important",
    borderRadius: "25px !important",
    boxShadow: "none !important",
  },
}));

const UpdateAssetModal = ({
  open,
  onClose,
  asset,
  categories,
  employeesList,
  onAssetUpdated
}) => {
  const classes = useStyle();
  const [tag, setTag] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [dateOfPurchase, setDateOfPurchase] = useState(null);
  const [price, setPrice] = useState("");
  const [dateOfWarranty, setDateOfWarranty] = useState(null);
  const [condition, setCondition] = useState("");
  const [availibilityStatus, setAvailibilityStatus] = useState("");
  const [issuedTo, setIssuedTo] = useState("");
  const [issueDate, setIssueDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [notes, setNotes] = useState("");
  const [assetDocs, setAssetDocs] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (asset) {
      setTag(asset.tag || "");
      setCategory(asset.category?._id || "");
      setBrand(asset.brand || "");
      setModel(asset.model || "");
      setDateOfPurchase(asset.date_of_purchase ? new Date(asset.date_of_purchase) : null);
      setPrice(asset.price || "");
      setDateOfWarranty(asset.date_of_warranty ? new Date(asset.date_of_warranty) : null);
      setCondition(asset.condition || "");
      setAvailibilityStatus(asset.availibility_status || "");
      setIssuedTo(asset.issued_to?._id || "");
      setIssueDate(asset.issue_date ? new Date(asset.issue_date) : null);
      setReturnDate(asset.return_date ? new Date(asset.return_date) : null);
      setNotes(asset.notes || "");
    }
  }, [asset]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = {
        tag,
        category,
        brand,
        model,
        date_of_purchase: dateOfPurchase ? moment(dateOfPurchase).format("YYYY-MM-DD") : null,
        price,
        date_of_warranty: dateOfWarranty ? moment(dateOfWarranty).format("YYYY-MM-DD") : null,
        condition,
        availibility_status: availibilityStatus,
        issued_to: issuedTo || null,
        issue_date: issueDate ? moment(issueDate).format("YYYY-MM-DD") : null,
        return_date: returnDate ? moment(returnDate).format("YYYY-MM-DD") : null,
        notes,
      };

      if (assetDocs) {
        formData.asset_docs = assetDocs;
      }

      const response = await api.put(`/asset/${asset._id}`, formData);

      if (response.status === 200) {
        successNotify("Asset updated successfully");
        onClose();
        if (onAssetUpdated) onAssetUpdated();
      }
    } catch (error) {
      errorNotify(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setAssetDocs(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={onClose}
      title="Update Asset"
      size="800"
      loading={loading}
    >
      <Form onSubmit={handleSubmit}>
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ my: 2 }}>
                <Form.Group>
                  <Form.Label>Tag</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Tag"
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    required
                  />
                </Form.Group>
              </Box>
            </Grid>
            
            <Grid item xs={6}>
              <Box sx={{ my: 2 }}>
                <Form.Group>
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories?.map((cat) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ my: 2 }}>
                <Form.Group>
                  <Form.Label>Brand</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Brand"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                  />
                </Form.Group>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ my: 2 }}>
                <Form.Group>
                  <Form.Label>Model</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Model"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                  />
                </Form.Group>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ my: 2 }}>
                <Form.Group>
                  <Form.Label>Date of Purchase</Form.Label>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={dateOfPurchase}
                      onChange={setDateOfPurchase}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Form.Group>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ my: 2 }}>
                <Form.Group>
                  <Form.Label>Price</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter Price"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </Form.Group>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ my: 2 }}>
                <Form.Group>
                  <Form.Label>Date of Warranty</Form.Label>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={dateOfWarranty}
                      onChange={setDateOfWarranty}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Form.Group>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ my: 2 }}>
                <Form.Group>
                  <Form.Label>Condition</Form.Label>
                  <Form.Select
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                    required
                  >
                    <option value="">Select Condition</option>
                    <option value="new">New</option>
                    <option value="used">Used</option>
                    <option value="damaged">Damaged</option>
                    <option value="repaired">Repaired</option>
                  </Form.Select>
                </Form.Group>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ my: 2 }}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={availibilityStatus}
                    onChange={(e) => setAvailibilityStatus(e.target.value)}
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="in stock">In Stock</option>
                    <option value="issued">Issued</option>
                    <option value="under maintenance">Under Maintenance</option>
                    <option value="lost">Lost</option>
                    <option value="scrapped">Scrapped</option>
                  </Form.Select>
                </Form.Group>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ my: 2 }}>
                <Form.Group>
                  <Form.Label>Issue Date</Form.Label>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={issueDate}
                      onChange={setIssueDate}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Form.Group>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ my: 2 }}>
                <Form.Group>
                  <Form.Label>Return Date</Form.Label>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={returnDate}
                      onChange={setReturnDate}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Form.Group>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ my: 2 }}>
                <Form.Group>
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Form.Group>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ my: 2 }}>
                <Form.Group>
                  <Form.Label>Asset Documents</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                  />
                </Form.Group>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <Box sx={{ display: "flex", gap: "10px" }}>
              <Button
                variant="contained"
                type="button"
                className={classes.cancelBtn}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                className={classes.createLeaveBtn}
                disabled={loading}
              >
                {loading ? "Updating..." : "Update Asset"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Form>
    </Modal>
  );
};

UpdateAssetModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  asset: PropTypes.object,
  categories: PropTypes.array.isRequired,
  employeesList: PropTypes.array.isRequired,
  onAssetUpdated: PropTypes.func
};

export default UpdateAssetModal;